import {
  builderImageSrcSet,
  builderImageUrl,
  builderImageWidths,
  isBuilderImageUrl,
} from "~/hec/utils/builder-image-url";

/**
 * Viewport-scope media queries for images that only ever display at one side
 * of the Tailwind `md:` breakpoint (768px — no `--breakpoint-md` override in
 * app.css). `MEDIA_MD_UP` matches where `hidden md:flex` containers show;
 * `MEDIA_BELOW_MD` is its exact complement (`md:hidden` containers).
 */
export const MEDIA_MD_UP = "(min-width: 768px)";
export const MEDIA_BELOW_MD = "not all and (min-width: 768px)";

/** 1x1 transparent GIF — a network-free `<source>` for viewports where a
 *  media-scoped image is CSS-hidden anyway. */
const BLANK_GIF =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

interface ImageProps {
  src: string | undefined;
  alt: string;
  className?: string;
  /**
   * Above-the-fold LCP candidates only (COM-163/COM-158): renders
   * `loading="eager"` + `fetchPriority="high"` so the browser fetches the
   * image immediately instead of waiting for layout. Default stays lazy.
   */
  priority?: boolean;
  /**
   * Media query for the viewports where this image is actually visible
   * (COM-163 review): heroes render separate desktop (`hidden md:flex`) and
   * mobile (`md:hidden`) variants, and an EAGER image downloads even inside
   * a `display: none` container — so `priority` alone would make phones pull
   * the desktop hero at high priority (and vice versa). When `media` is set,
   * the real `<picture>` sources are scoped to it and out-of-scope viewports
   * resolve to an inline pixel instead (no network). Lazy images in hidden
   * containers never intersect, so for those this is a no-op either way.
   */
  media?: string;
  /**
   * Largest CSS-pixel width this image is shown at. Builder CDN requests
   * go out at 1×/2× of this (capped). Ignored for non-Builder URLs.
   */
  maxCssWidth?: number;
  sizes?: string;
  loading?: "lazy" | "eager";
}

const DEFAULT_MAX_CSS_WIDTH = 800;

const Image = ({
  src,
  alt,
  className,
  priority = false,
  media,
  maxCssWidth = DEFAULT_MAX_CSS_WIDTH,
  sizes,
  loading = "lazy",
}: ImageProps) => {
  const imageSrc = src || "https://placehold.co/400x400/png";
  const imgClass = className ?? "h-full w-full object-cover";
  // COM-163 semantics compose with the explicit `loading` prop: `priority`
  // always wins (it exists to mark the LCP candidate), otherwise the caller's
  // `loading` (default lazy) applies.
  const effectiveLoading = priority ? "eager" : loading;
  const fetchPriority = priority ? ("high" as const) : undefined;

  if (isBuilderImageUrl(imageSrc)) {
    const widths = builderImageWidths(maxCssWidth);
    const largest = widths[widths.length - 1];
    const builderSrc = builderImageUrl(imageSrc, {
      width: largest,
      format: "webp",
    });
    const builderSrcSet = builderImageSrcSet(imageSrc, widths);
    if (media) {
      // Heroes pass Builder URLs — the media scoping (see the `media` doc
      // comment) must apply here too, or an eager desktop hero downloads on
      // phones at high priority.
      return (
        <picture>
          <source media={media} srcSet={builderSrcSet} sizes={sizes} />
          {/* Catch-all for out-of-scope viewports: no media = always matches,
              so the picture resolves to a data URI and skips the real asset. */}
          <source srcSet={BLANK_GIF} />
          <img
            src={builderSrc}
            alt={alt}
            className={imgClass}
            loading={effectiveLoading}
            fetchPriority={fetchPriority}
          />
        </picture>
      );
    }
    return (
      <img
        src={builderSrc}
        srcSet={builderSrcSet}
        sizes={sizes}
        alt={alt}
        className={imgClass}
        loading={effectiveLoading}
        fetchPriority={fetchPriority}
      />
    );
  }

  return (
    <picture>
      <source
        type="image/webp"
        media={media}
        srcSet={imageSrc.replace(/\.(jpg|png)$/i, ".webp")}
      />
      <source type="image/jpeg" media={media} srcSet={imageSrc} />
      {/* Catch-all for out-of-scope viewports: no media/type = always matches,
          so the picture resolves to a data URI and skips the real asset. */}
      {media ? <source srcSet={BLANK_GIF} /> : null}
      <img
        src={imageSrc}
        alt={alt}
        className={imgClass}
        loading={effectiveLoading}
        fetchPriority={fetchPriority}
      />
    </picture>
  );
};

export default Image;
